<script setup lang="ts">
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/UserStore'
import { ref } from 'vue'
import { User } from '@/types'
import { useI18n } from 'vue-i18n'
import { authApi } from '@/api/client'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardFooter,
} from '@/components/ui/card'

const { t } = useI18n()
const userStore = useUserStore()
const router = useRouter()

const showErrorMessage = ref(false)

const email = ref<string>('')
const password = ref<string>('')

const onSubmit = async (_event: Event) => {
    showErrorMessage.value = false

    await authApi.csrfCookie()

    try {
        const loginResponse = await authApi.login(email.value, password.value)

        if (loginResponse.data.two_factor) {
            console.error('Unsupported authentication method')
        }

        const userResponse = await authApi.user()
        userStore.login(userResponse.data as User)

        await router.push({ name: 'dashboard.catalog' })
    } catch {
        showErrorMessage.value = true
    }
}
</script>

<template>
    <div class="flex min-h-svh w-full items-center justify-center p-6 md:p-10">
        <div class="w-full max-w-sm">
        <Card>
            <CardHeader>
                <CardTitle class="text-2xl">Welcome back</CardTitle>
                <CardDescription>
                    Enter your credentials to access the dashboard.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <form class="space-y-4" @submit.prevent="onSubmit">
                    <div class="space-y-2">
                        <Label for="email">{{ t('login.labels.email') }}</Label>
                        <Input
                            id="email"
                            v-model="email"
                            autocomplete="username"
                            type="email"
                            name="email"
                            placeholder="name@company.com"
                            required
                        />
                    </div>
                    <div class="space-y-2">
                        <Label for="password">{{ t('login.labels.password') }}</Label>
                        <Input
                            id="password"
                            v-model="password"
                            autocomplete="current-password"
                            type="password"
                            name="password"
                            placeholder="••••••••"
                            required
                        />
                    </div>

                    <div
                        v-if="showErrorMessage"
                        class="text-sm text-destructive"
                        role="alert"
                    >
                        {{ t('login.errors.invalidCredentials') }}
                    </div>

                    <Button type="submit" class="w-full">
                        {{ t('login.enter') }}
                    </Button>
                </form>
            </CardContent>
            <CardFooter>
                <p class="w-full text-center text-sm text-muted-foreground">
                    {{ t('login.noAccount') }}
                    <a
                        href="mailto:support@recommerceiq.com"
                        target="_blank"
                        class="font-medium underline-offset-4 hover:underline"
                    >{{ t('login.contactUs') }}</a>
                </p>
            </CardFooter>
        </Card>
        </div>
    </div>
</template>
